import { useApp, useSetContentStore, useDragging, useEditorSync, useMedia, useSectionStore, useSelectionReference, useUploadJobs, useS3Client, useDeletedContent, useDeletedSections, useStructureDirty, useContentStructure } from "@/hooks/contextHooks"
import { Accordion, Button, Card, Center, Divider, Flex, Heading, Spinner, Box, useColorModeValue, HStack, Tooltip, IconButton, Text, Avatar, AvatarGroup, Input, InputGroup, InputLeftElement, InputRightElement } from "@chakra-ui/react"
import { faCancel, faSave, faQuestionCircle, faVideo, faBolt, faExpand, faCompress, faCheck, faFolderPlus, faSearch, faTimes, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useNavigate } from "react-router-dom"
import { NewSection, Section, SectionMeta, NewContentItem, Content, NewMediaReference } from "@xnav/types/src/common"
import AddSectionButton from "./addSectionButton"
import SectionCard from "./sectionCard"
import React, { memo, useEffect, useState, useRef, useMemo, useContext } from "react"
// import Draggable from "./draggable"
import Draggable from 'react-draggable';
import { DraggingContext, DraggingContextType } from "@/contexts/draggingContext"
import { createClientContent, validateNameisUnique } from "@/functions/editorFunctions"
import { ContentStructureRequest, EditContent, SelectionType } from "@/pages/editor"
import { apiGetRequest, apiPatchRequest, logCourseAction } from "@/functions/requests"
import Loading from "./loading"
import { DeletedContentContext, DeletedSectionsContext, NavCollapsedContext } from "@/contexts/contexts"
import { ReactStateType } from "@xnav/types/src/frontend"
import { MediaMeta } from "@/pages/mediaManager"
import { GetObjectCommand, S3Client } from "@aws-sdk/client-s3"
import { useAuthenticator } from "@aws-amplify/ui-react"
import { getSignedUrl } from "@aws-sdk/s3-request-presigner"
import { v4 } from "uuid"


function flattenStructure(sections: NewSection[], parentId?: string): Record<string, { title: string, type: string, parentId?: string }> {
	const map: Record<string, { title: string, type: string, parentId?: string }> = {};
	
	function traverse(item: NewContentItem, pId?: string) {
		if (!item) return;
		map[item.id] = { title: item.title, type: item.type, parentId: pId };
		if (item.type === 'section' && 'contents' in item && item.contents) {
			item.contents.forEach(child => traverse(child, item.id));
		}
	}
	
	sections.forEach(s => traverse(s, parentId));
	return map;
}

function moveItemInTree(
	tree: NewSection[],
	draggedId: string,
	targetId: string,
	position: 'inside' | 'before' | 'after'
): NewSection[] {
	const newTree = JSON.parse(JSON.stringify(tree)) as NewSection[];
	let draggedItem: NewContentItem | null = null;

	// Remove dragged item from tree
	function removeDragged(list: (NewSection | NewMediaReference)[]): (NewSection | NewMediaReference)[] {
		const result: (NewSection | NewMediaReference)[] = [];
		for (const item of list) {
			if (item.id === draggedId) {
				draggedItem = item;
				continue;
			}
			if (item.type === 'section') {
				const sec = item as NewSection;
				result.push({
					...sec,
					contents: removeDragged(sec.contents)
				});
			} else {
				result.push(item);
			}
		}
		return result;
	}

	const treeAfterRemoval = removeDragged(newTree) as NewSection[];
	if (!draggedItem) {
		return tree; // dragged item not found
	}

	// Insert dragged item relative to target
	function insertItem(
		list: (NewSection | NewMediaReference)[]
	): { list: (NewSection | NewMediaReference)[]; inserted: boolean } {
		const newList: (NewSection | NewMediaReference)[] = [];
		let inserted = false;

		const targetIdx = list.findIndex(item => item.id === targetId);

		if (targetIdx !== -1) {
			const targetItem = list[targetIdx];
			for (let i = 0; i < list.length; i++) {
				const item = list[i];
				if (i === targetIdx) {
					if (position === 'before') {
						newList.push(draggedItem!);
						newList.push(item);
						inserted = true;
					} else if (position === 'after') {
						newList.push(item);
						newList.push(draggedItem!);
						inserted = true;
					} else { // 'inside'
						if (item.type === 'section') {
							const sec = item as NewSection;
							newList.push({
								...sec,
								contents: [...sec.contents, draggedItem!]
							});
							inserted = true;
						} else {
							// Target not a section, place after it
							newList.push(item);
							newList.push(draggedItem!);
							inserted = true;
						}
					}
				} else {
					if (item.type === 'section') {
						const sec = item as NewSection;
						const res = insertItem(sec.contents);
						if (res.inserted) {
							newList.push({
								...sec,
								contents: res.list
							});
							inserted = true;
						} else {
							newList.push(item);
						}
					} else {
						newList.push(item);
					}
				}
			}
			return { list: newList, inserted };
		}

		for (const item of list) {
			if (item.type === 'section') {
				const sec = item as NewSection;
				const res = insertItem(sec.contents);
				if (res.inserted) {
					newList.push({
						...sec,
						contents: res.list
					});
					inserted = true;
				} else {
					newList.push(item);
				}
			} else {
				newList.push(item);
			}
		}

		return { list: newList, inserted };
	}

	// Try inserting at root level first
	const rootIdx = treeAfterRemoval.findIndex(item => item.id === targetId);
	if (rootIdx !== -1) {
		const list = [...treeAfterRemoval];
		const targetItem = list[rootIdx];
		if (position === 'before') {
			list.splice(rootIdx, 0, draggedItem);
		} else if (position === 'after') {
			list.splice(rootIdx + 1, 0, draggedItem);
		} else { // 'inside'
			if (targetItem.type === 'section') {
				list[rootIdx] = {
					...targetItem,
					contents: [...targetItem.contents, draggedItem]
				};
			} else {
				list.splice(rootIdx + 1, 0, draggedItem);
			}
		}
		return list;
	}

	const res = insertItem(treeAfterRemoval);
	return res.inserted ? (res.list as NewSection[]) : treeAfterRemoval;
}

const SectionNavigator = memo(function SectionNavigator() {
	const app = useApp()
	const navigate = useNavigate()
	const [, setMedia] = useMedia() as ReactStateType<MediaMeta[] | undefined>

	const [sectionMeta, setSectionMeta] = useContentStructure() as ReactStateType<NewSection[] | undefined>
	const [deletedContent, setDeletedContent] = useDeletedContent() as ReactStateType<string[]>
	const [deletedSections, setDeletedSections] = useDeletedSections() as ReactStateType<string[]>
	const [structureDirty, setStructureDirty] = useStructureDirty() as ReactStateType<boolean>
	const [selectionReference, setSelectionReference] = useSelectionReference() as ReactStateType<SelectionType | undefined>
	const setContentStore = useSetContentStore()
	const [sectionStore, setSectionStore] = useSectionStore() as ReactStateType<Record<string, Section>>
	const [initialStructure, setInitialStructure] = useState<string>('');

	const [dragging, setDragging] = useState<DraggingContextType>()
	const [editorSync, setEditorSync] = useEditorSync()
	const [pushJob, popJob] = useUploadJobs()
	const s3 = useS3Client() as S3Client
	const { user } = useAuthenticator()
	const [isOverRootDropZone, setIsOverRootDropZone] = useState(false)

	const [isAddingSection, setIsAddingSection] = useState(false)
	const [newSectionTitle, setNewSectionTitle] = useState('')
	const [searchQuery, setSearchQuery] = useState('')
	const [isNavCollapsed, setIsNavCollapsed] = useContext(NavCollapsedContext)

	const hasStructureChanges = useMemo(() => {
		if (!sectionMeta || !initialStructure) return false;
		return JSON.stringify(sectionMeta) !== initialStructure;
	}, [sectionMeta, initialStructure]);

	useEffect(() => {
		setStructureDirty(hasStructureChanges);
	}, [hasStructureChanges, setStructureDirty]);

	function filterTree(items: NewSection[], query: string): NewSection[] {
		if (!query) return items;
		const lowerQuery = query.toLowerCase();
		return items
			.map(item => {
				const matchesSelf = item.title.toLowerCase().includes(lowerQuery);
				const filteredContents = item.contents ? filterTreeContents(item.contents, query) : [];
				if (matchesSelf || filteredContents.length > 0) {
					return {
						...item,
						contents: filteredContents
					};
				}
				return null;
			})
			.filter((item): item is NewSection => item !== null);
	}

	function filterTreeContents(contents: NewContentItem[], query: string): NewContentItem[] {
		const lowerQuery = query.toLowerCase();
		return contents
			.map(item => {
				if (item.type === 'section') {
					const section = item as NewSection;
					const matchesSelf = section.title.toLowerCase().includes(lowerQuery);
					const filteredSubContents = filterTreeContents(section.contents, query);
					if (matchesSelf || filteredSubContents.length > 0) {
						return {
							...section,
							contents: filteredSubContents
						};
					}
					return null;
				} else {
					if (item.title.toLowerCase().includes(lowerQuery)) {
						return item;
					}
					return null;
				}
			})
			.filter((item): item is NewContentItem => item !== null);
	}

	const filteredSectionMeta = useMemo(() => {
		if (!sectionMeta) return [];
		return filterTree(sectionMeta, searchQuery);
	}, [sectionMeta, searchQuery]);

	useEffect(() => {
		if (searchQuery && filteredSectionMeta) {
			setExpandedIndices(Array.from({ length: filteredSectionMeta.length }, (_, i) => i));
		}
	}, [searchQuery, filteredSectionMeta]);

	useEffect(() => {
		const handleNewSection = () => {
			setIsAddingSection(true);
		};
		window.addEventListener('editor-new-section', handleNewSection);
		return () => {
			window.removeEventListener('editor-new-section', handleNewSection);
		};
	}, []);

	const [userRole, setUserRole] = useState<string | null>('Instructor');
	useEffect(() => {
		const fetchRole = async () => {
			if (user?.signInDetails?.loginId) {
				try {
					const data = await apiGetRequest<undefined, { role: string }>(`/users/${user.signInDetails.loginId}`);
					setUserRole(data?.role || 'Instructor');
				} catch (error) {
					setUserRole('Instructor');
				}
			}
		};
		fetchRole();
	}, [user]);

	const isCreatorOrCollab = userRole === 'Super Admin' || userRole === 'Admin' || app.creatorId === user?.userId || 
		(user?.userId && app.collaborators?.includes(user?.userId)) || 
		(user?.signInDetails?.loginId && app.collaborators?.some(c => c.toLowerCase() === user.signInDetails.loginId.toLowerCase()));
	
	const isOnlyReviewer = !isCreatorOrCollab && (
		(user?.userId && app.reviewers?.includes(user?.userId)) || 
		(user?.signInDetails?.loginId && app.reviewers?.some(r => r.toLowerCase() === user.signInDetails.loginId.toLowerCase()))
	);

	const [expandedIndices, setExpandedIndices] = useState<number[]>([])

	useEffect(() => {
		if (selectionReference && sectionMeta) {
			const index = sectionMeta.findIndex(section => {
				const hasItem = (s: NewSection): boolean => {
					if (s.id === selectionReference.id) return true;
					if (s.contents) {
						for (const c of s.contents) {
							if (c.id === selectionReference.id) return true;
							if (c.type === 'section' && hasItem(c as NewSection)) return true;
						}
					}
					return false;
				};
				return hasItem(section);
			});
			if (index !== -1 && !expandedIndices.includes(index)) {
				setExpandedIndices(prev => [...prev, index]);
			}
		}
	}, [selectionReference, sectionMeta]);

	function getTotalSectionsCount(sections: NewSection[]): number {
		let count = 0;
		sections.forEach(s => {
			count++;
			if (s.contents) {
				count += getTotalSectionsCount(s.contents.filter(c => c.type === 'section') as NewSection[]);
			}
		});
		return count;
	}

	function toggleExpandAll() {
		if (sectionMeta) {
			const total = getTotalSectionsCount(sectionMeta);
			if (expandedIndices.length >= total && total > 0) {
				setExpandedIndices([]);
			} else {
				setExpandedIndices(Array.from({ length: total }, (_, i) => i));
			}
		}
	}

	async function preloadAllContentStore(sections: NewSection[]) {
		if (!sections || !app?.id) return;
		const items: NewContentItem[] = [];
		function collect(list: NewContentItem[]) {
			for (const item of list) {
				if (item.type === 'section' && (item as NewSection).contents) {
					collect((item as NewSection).contents);
				} else if (item.type === 'video') {
					items.push(item);
				}
			}
		}
		sections.forEach(s => {
			if (s.contents) collect(s.contents);
		});

		if (items.length === 0) return;

		const newStoreEntries: Record<string, EditContent> = {};
		await Promise.all(items.map(async (item) => {
			try {
				const command = new GetObjectCommand({
					Bucket: import.meta.env.VITE_EDIT_BUCKET_NAME,
					Key: `${app.id}/contents/content/${item.id}.json`
				});
				const url = await getSignedUrl(s3, command, { expiresIn: 3600 });
				const res = await fetch(url);
				if (res.ok) {
					let data = await res.text();
					try {
						data = JSON.parse(data);
						if (typeof data === 'string') data = JSON.parse(data);
					} catch {}
					const client = createClientContent(data as any);
					if (client && item.id) {
						if (!client.id) client.id = item.id;
						if (!client.title) client.title = item.title;
						if ((item as any).videoId && !client.videoId) client.videoId = (item as any).videoId;
						if ((item as any).videoTitle && !client.videoTitle) client.videoTitle = (item as any).videoTitle;
						newStoreEntries[item.id] = client;
					}
				}
			} catch (err) {
				// Ignore missing JSONs
			}
		}));

		if (Object.keys(newStoreEntries).length > 0) {
			setContentStore(prev => ({
				...prev,
				...newStoreEntries
			}));
		}
	}

	async function fetchContentStructure() {
		try {
			const data = await apiGetRequest<string, NewSection[]>(`/structure/${app.id}`);
			setSectionMeta(data);
			setInitialStructure(JSON.stringify(data));
			preloadAllContentStore(data);
		} catch {
			setSectionMeta([]);
			setInitialStructure('[]');
		}
	}

	function onReset() {
		setSelectionReference(undefined)
		setContentStore({})
		setSectionStore({})
		setDeletedContent([])
		setDeletedSections([])
		setStructureDirty(false)
		fetchContentStructure()
		setMedia(undefined)
	}

	async function pushContentStructure() {
		pushJob()
		try {
			await apiPatchRequest<ContentStructureRequest, SectionMeta[]>(`/structure/${app.id}`, { newStructure: sectionMeta!, deletedContent, deletedSections })
			
			try {
				const initialParsed = initialStructure ? JSON.parse(initialStructure) : [];
				const initialMap = flattenStructure(initialParsed);
				const currentMap = flattenStructure(sectionMeta || []);
				
				const logDetails: string[] = [];
				
				// Detect additions and renames
				Object.entries(currentMap).forEach(([id, current]) => {
					const initial = initialMap[id];
					if (!initial) {
						logDetails.push(`Added ${current.type}: "${current.title}"`);
					} else if (initial.title !== current.title) {
						logDetails.push(`Renamed ${current.type} "${initial.title}" to "${current.title}"`);
					}
				});
				
				// Detect deletions
				Object.entries(initialMap).forEach(([id, initial]) => {
					if (!currentMap[id]) {
						logDetails.push(`Deleted ${initial.type}: "${initial.title}"`);
					}
				});
				
				if (logDetails.length > 0) {
					await logCourseAction(app.id, "Updated Course Structure", logDetails.join(", "));
				}
			} catch (e) {
				console.error("Failed to log structure changes", e);
			}

			setInitialStructure(JSON.stringify(sectionMeta));
			setDeletedContent([])
			setDeletedSections([])
			setStructureDirty(false)
		} catch (err) {
			console.error("Failed to save content structure:", err);
		} finally {
			popJob()
		}
	}

	const handleMoveItem = (draggedId: string, targetId: string, position: 'inside' | 'before' | 'after') => {
		if (draggedId === targetId) return
		setStructureDirty(true)
		setSectionMeta(prev => {
			if (!prev) return prev
			return moveItemInTree(prev, draggedId, targetId, position)
		})
	}

	useEffect(() => {
		fetchContentStructure()
	}, [])

	useEffect(() => {
		if (editorSync) pushContentStructure()
	}, [editorSync])

	useEffect(() => {
		if (!deletedContent.length) return
		console.log("Deleted content updated in parent:", deletedContent);

		// setMedia(mediaMeta => mediaMeta!.map(meta => ({
		// 	...meta,
		// 	references: meta.references.filter(reference => reference !== deletedContent[deletedContent.length - 1])
		// })))
		setMedia(mediaMeta =>
			mediaMeta!.map(meta => ({
				...meta,
				references: meta.references.filter(ref => !deletedContent.includes(ref))
			}))
		);
	}, [deletedContent])

	useEffect(() => {
		if (!deletedSections.length) return
		console.log("Deleted section updated in parent:", deletedSections);
		// setMedia(mediaMeta => mediaMeta!.map(meta => ({
		// 	...meta,
		// 	references: meta.references.filter(reference => reference !== deletedSections[deletedSections.length - 1])
		// })))

		setMedia(mediaMeta =>
			mediaMeta!.map(meta => ({
				...meta,
				references: meta.references.filter(ref => !deletedSections.includes(ref))
			}))
		);
	}, [deletedSections])
	useEffect(() => {
		console.log('selectionReference', selectionReference)
	}, [selectionReference])

	return (
		<DraggingContext.Provider value={[dragging, setDragging]}>
					<Card
						variant='elevated'
						borderRadius='0'
						borderRightWidth='1px'
						borderColor={useColorModeValue('gray.200', 'gray.700')}
						m={0}
						h='100%'
						flexDir='column'
						w={isNavCollapsed ? "52px" : "350px"}
						minW={isNavCollapsed ? "52px" : "350px"}
						transition="all 0.25s cubic-bezier(0.2, 0, 0, 1)"
						overflow="hidden"
						bg={useColorModeValue('gray.50', 'gray.900')}
						shadow="sm"
					>
						{isNavCollapsed ? (
							<Flex flexDir="column" align="center" py={4} gap={4} h="full" bg={useColorModeValue('gray.100', 'gray.850')}>
								<Tooltip label="Expand Outline Navigator" placement="right" hasArrow>
									<IconButton
										aria-label="Expand outline"
										icon={<FontAwesomeIcon icon={faChevronRight} />}
										size="sm"
										colorScheme="blue"
										variant="solid"
										onClick={() => setIsNavCollapsed(false)}
										borderRadius="lg"
										shadow="sm"
									/>
								</Tooltip>
								<Divider borderColor={useColorModeValue('gray.300', 'gray.700')} />
								<Tooltip label="Outline Navigator (Click to expand)" placement="right" hasArrow>
									<Flex
										flex={1}
										align="center"
										justify="center"
										cursor="pointer"
										onClick={() => setIsNavCollapsed(false)}
										py={2}
										w="full"
									>
										<Text
											transform="rotate(-90deg)"
											whiteSpace="nowrap"
											fontSize="xs"
											fontWeight="bold"
											letterSpacing="widest"
											color={useColorModeValue('gray.500', 'gray.400')}
											textTransform="uppercase"
										>
											Outline & Sections
										</Text>
									</Flex>
								</Tooltip>
							</Flex>
						) : !sectionMeta ? (
							<Center flex={1}>
								<Loading message='Fetching contents...' />
							</Center>
						) : (
							<Flex flex={1} flexDir='column' overflow='hidden'>
								<Flex p={3} px={4} align="center" gap={2} bg={useColorModeValue('gray.100', 'gray.850')} borderBottomWidth={1} borderColor={useColorModeValue('gray.200', 'gray.700')}>
									<InputGroup size="sm" flex={1}>
										<InputLeftElement pointerEvents="none">
											<FontAwesomeIcon icon={faSearch} color="gray.400" />
										</InputLeftElement>
										<Input 
											placeholder="Search outline..." 
											value={searchQuery}
											onChange={(e) => setSearchQuery(e.target.value)}
											borderRadius="xl"
											bg={useColorModeValue("white", "gray.800")}
											pr={searchQuery ? "95px" : "70px"}
											shadow="xs"
										/>
										<InputRightElement w="auto" pr={1} gap={1} display="flex" alignItems="center">
											{searchQuery && (
												<IconButton 
													size="xs" 
													variant="ghost" 
													aria-label="Clear Search" 
													icon={<FontAwesomeIcon icon={faTimes} />} 
													onClick={() => setSearchQuery("")} 
												/>
											)}
											<Tooltip label={expandedIndices.length > 0 ? "Collapse All Sections" : "Expand All Sections"} placement="top" hasArrow>
												<IconButton
													size="xs"
													variant="ghost"
													colorScheme="gray"
													aria-label={expandedIndices.length > 0 ? "Collapse All" : "Expand All"}
													icon={<FontAwesomeIcon icon={expandedIndices.length > 0 ? faCompress : faExpand} />}
													onClick={toggleExpandAll}
													_hover={{ bg: useColorModeValue('gray.200', 'gray.700') }}
												/>
											</Tooltip>
											{!isOnlyReviewer && (
												<Tooltip label="New Section" placement="top" hasArrow>
													<IconButton
														size="xs"
														colorScheme="blue"
														variant="solid"
														aria-label="New Section"
														icon={<FontAwesomeIcon icon={faFolderPlus} />}
														onClick={() => setIsAddingSection(true)}
														borderRadius="md"
													/>
												</Tooltip>
											)}
										</InputRightElement>
									</InputGroup>
									<Tooltip label="Collapse sidebar to maximize editor space" placement="top" hasArrow>
										<IconButton
											aria-label="Collapse navigator"
											icon={<FontAwesomeIcon icon={faChevronLeft} />}
											size="sm"
											variant="ghost"
											colorScheme="gray"
											onClick={() => setIsNavCollapsed(true)}
											borderRadius="lg"
											_hover={{ bg: useColorModeValue('gray.200', 'gray.700'), color: 'blue.500' }}
										/>
									</Tooltip>
								</Flex>
								<Box flex={1} overflowY='auto' p={2} bg={useColorModeValue('gray.50', 'gray.900')}>
									{isAddingSection && (
										<Flex w='full' gap={2} p={2} mb={3} bg="white" _dark={{ bg: "gray.850" }} borderRadius="xl" border="1px solid" borderColor="blue.200" align="center">
											<Input 
												size="sm"
												variant="filled"
												borderRadius="md" 
												placeholder='New section name...' 
												autoFocus={true} 
												value={newSectionTitle} 
												onChange={(e) => setNewSectionTitle(e.target.value)} 
												flex={1} 
											/>
											<IconButton size="sm" colorScheme="red" variant='ghost' aria-label="Cancel" icon={<FontAwesomeIcon icon={faCancel} />} onClick={() => { setIsAddingSection(false); setNewSectionTitle(''); }} />
											<IconButton size="sm" colorScheme="blue" isDisabled={!newSectionTitle || sectionMeta?.some(s => s.title === newSectionTitle)} aria-label="Confirm" icon={<FontAwesomeIcon icon={faCheck} />} onClick={() => {
												setSectionMeta(prev => [...prev!, {
													id: v4(),
													title: newSectionTitle,
													type: 'section',
													contents: []
												}]);
												setIsAddingSection(false);
												setNewSectionTitle('');
											}} />
										</Flex>
									)}
									<Accordion 
										variant='fileNav' 
										allowMultiple 
										index={expandedIndices} 
										onChange={(indices) => setExpandedIndices(indices as number[])}
									>
										{filteredSectionMeta.map((section, index) => (
											<SectionCard
												key={section.title}
												sectionMeta={section}
												deletedContent={deletedContent}
												index={index}
												rootSection={true}
												validateName={(name) => validateNameisUnique(name, sectionMeta.map(sm => sm.title))}
												onChange={newSection => setSectionMeta(sectionMeta => sectionMeta!.map(s => s.id === newSection.id ? newSection : s))}
												onMoveItem={handleMoveItem}
												searchQuery={searchQuery}
												onDelete={index => {
													const toDelete = sectionMeta?.[index]
													console.log("toDelete", toDelete)
													if (!toDelete) return

													// Push section ID to deletedSections
													setDeletedSections(prev => [...prev, toDelete.id])

													// Push each content ID in the section to deletedContent
													if (toDelete.contents) {
														setDeletedContent(prev => [
															...prev,
															...toDelete.contents.map(c => c.id)
														])
													}

													// Remove the section
													setSectionMeta(sectionMeta => sectionMeta ? sectionMeta.filter((_, i) => i !== index) : undefined)
												}}
											/>
										))}
									</Accordion>
									{!isOnlyReviewer && (
										<Box
											p={4}
											mt={4}
											border="2px dashed"
											borderColor={isOverRootDropZone ? "cyan.400" : "gray.200"}
											_dark={{ borderColor: isOverRootDropZone ? "cyan.400" : "gray.700" }}
											borderRadius="lg"
											textAlign="center"
											onDragOver={(e) => {
												e.preventDefault();
												setIsOverRootDropZone(true);
											}}
											onDragLeave={() => {
												setIsOverRootDropZone(false);
											}}
											onDrop={(e) => {
												e.preventDefault();
												setIsOverRootDropZone(false);
												const draggedId = e.dataTransfer.getData("text/plain") || (dragging as any)?.id;
												if (draggedId) {
													setSectionMeta(prev => {
														if (!prev) return prev;
														const lastRootItem = prev[prev.length - 1];
														if (lastRootItem) {
															return moveItemInTree(prev, draggedId, lastRootItem.id, 'after');
														}
														return prev;
													});
												}
											}}
											bg={isOverRootDropZone ? useColorModeValue("cyan.50", "rgba(6, 182, 212, 0.15)") : "transparent"}
											transition="all 0.2s"
										>
											<Text fontSize="xs" color="gray.500" _dark={{ color: "gray.400" }} pointerEvents="none">
												Drag files/folders here to move to root level
											</Text>
										</Box>
									)}
								</Box>
							</Flex>
						)}

					</Card>
				</DraggingContext.Provider>
	)
})

export default SectionNavigator
